/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-05-27 09:30:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, Heading, SkeletonBodyText, Icon, Tooltip } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Filter from 'src/components/containers/analytics-v2/filter';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import { QUERY_EXECUTION_KEY, FEATURES } from 'src/constants/analytics';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { formatMoney } from 'src/helpers/money-format';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';
import { CaretUpMinor, CaretDownMinor } from '@shopify/polaris-icons';
import { formatNumberInThousand } from 'src/helpers';
import history from 'src/routes/history';
import { routes } from 'src/routes';

import IconUp1 from 'src/assets/images/dashboard-v3/app-insight/up/up1.svg';
import IconUp2 from 'src/assets/images/dashboard-v3/app-insight/up/up2.svg';
import IconUp3 from 'src/assets/images/dashboard-v3/app-insight/up/up3.svg';
import IconUp4 from 'src/assets/images/dashboard-v3/app-insight/up/up4.svg';
import IconUp5 from 'src/assets/images/dashboard-v3/app-insight/up/up5.svg';
import IconUp6 from 'src/assets/images/dashboard-v3/app-insight/up/up6.svg';
import IconUp7 from 'src/assets/images/dashboard-v3/app-insight/up/up7.svg';
import IconUp8 from 'src/assets/images/dashboard-v3/app-insight/up/up8.svg';
import IconUp9 from 'src/assets/images/dashboard-v3/app-insight/up/up9.svg';
import IconUp10 from 'src/assets/images/dashboard-v3/app-insight/up/up10.svg';
import IconDown1 from 'src/assets/images/dashboard-v3/app-insight/down/down1.svg';
import IconDown2 from 'src/assets/images/dashboard-v3/app-insight/down/down2.svg';
import IconDown3 from 'src/assets/images/dashboard-v3/app-insight/down/down3.svg';
import IconDown4 from 'src/assets/images/dashboard-v3/app-insight/down/down4.svg';
import IconDown5 from 'src/assets/images/dashboard-v3/app-insight/down/down5.svg';
import IconDown6 from 'src/assets/images/dashboard-v3/app-insight/down/down6.svg';
import IconDown7 from 'src/assets/images/dashboard-v3/app-insight/down/down7.svg';
import IconDown8 from 'src/assets/images/dashboard-v3/app-insight/down/down8.svg';
import IconDown9 from 'src/assets/images/dashboard-v3/app-insight/down/down9.svg';
import IconDown10 from 'src/assets/images/dashboard-v3/app-insight/down/down10.svg';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {};

const fromDateStateName = 'appInsightFromDate';
const toDateStateName = 'appInsightToDate';
const compareStateName = 'appInsightCompare';

const IconsUp = [
  IconUp1,
  IconUp2,
  IconUp3,
  IconUp4,
  IconUp5,
  IconUp6,
  IconUp7,
  IconUp8,
  IconUp9,
  IconUp10
];

const IconsDown = [
  IconDown1,
  IconDown2,
  IconDown3,
  IconDown4,
  IconDown5,
  IconDown6,
  IconDown7,
  IconDown8,
  IconDown9,
  IconDown10
];

class AppInsight extends Component<ComposedProps, State> {
  state = {};

  componentDidMount() {
    const { fromDate, toDate } = this.props;
    if (fromDate && toDate) {
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);

      this.fetchAll(fromDateUTC, toDateUTC, true);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate } = this.props;
    const { fromDate: nextFromDate, toDate: nextToDate } = nextProps;

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      onAbortApiAnalytic();
      setTimeout(() => {
        this.fetchAll(fromDateUTC, toDateUTC, true);
      }, 500);
    }

    // Update execution ids
    this.processUpdateExecutionId(this.props, nextProps);
  }

  renderIndexRandom = () => {
    const arr = _.shuffle([...new Array(10)].map((i, idx) => idx));
    return arr.splice(0, 4);
  };

  renderItem = (title, tooltip, value, percentGrowthChange, loading, idx) => {
    const { i18n } = this.props;
    const caretIcon =
      percentGrowthChange > 0 ? CaretUpMinor : percentGrowthChange < 0 ? CaretDownMinor : null;

    if (loading) {
      return (
        <div className="item">
          <div className="title">
            <Tooltip content={i18n.translate(tooltip)} preferredPosition="above">
              <p>{i18n.translate(title)}</p>
            </Tooltip>
          </div>
          <div className="img">
            <SkeletonBodyText lines={2} />
          </div>
          <SkeletonBodyText lines={2} />
        </div>
      );
    }

    return (
      <div className="item">
        <div className="title">
          <Tooltip content={i18n.translate(tooltip)} preferredPosition="above">
            <p>{i18n.translate(title)}</p>
          </Tooltip>
        </div>
        <div className="img">
          {percentGrowthChange > 0 ? (
            <img width="100%" alt="icon" src={IconsUp[idx]} />
          ) : percentGrowthChange < 0 ? (
            <img width="100%" alt="icon" src={IconsDown[idx]} />
          ) : null}
        </div>
        <div className="value">{value}</div>
        <div className="percent">
          {!!percentGrowthChange ? (
            <div
              className={`percent-change ${
                percentGrowthChange > 0 ? 'up' : percentGrowthChange < 0 ? 'down' : ''
              }`}>
              <span>{Math.abs(percentGrowthChange)}%</span>
              <Icon source={caretIcon} />
            </div>
          ) : (
            <div className="percent-change">-</div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      openReviewAddonModal,
      performActionSurvey,
      i18n,
      totalActivityTotal,
      totalActivityPercentChange,
      totalActivityLoading,
      totalRevenueTotal,
      totalRevenuePercentChange,
      totalRevenueLoading,
      orderCountTotal,
      orderCountPercentChange,
      orderCountLoading,
      totalConversionRateTotal,
      totalConversionRatePercentChange,
      totalConversionRateLoading,
      moneyFormat,
      tenantApp
    } = this.props;
    const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
    const currentMetaDataAnalytic = _.get(tenantApp, 'analyticsSettings', {});
    const isFirstHasValueRevenue = _.get(currentMetaDataAnalytic, 'isFirstHasValueRevenue', false);
    const isFirstHasValueActivity = _.get(
      currentMetaDataAnalytic,
      'isFirstHasValueActivity',
      false
    );

    // Paid user: when the revenue is > 0
    if (
      !!selectedPlan &&
      !isFirstHasValueRevenue &&
      !isFirstHasValueActivity &&
      !totalRevenueTotal
    ) {
      return <></>;
    }

    // Trial user: when the total app activity is > 0
    if (
      !selectedPlan &&
      !isFirstHasValueRevenue &&
      !isFirstHasValueActivity &&
      !totalActivityTotal
    ) {
      return <></>;
    }

    const idxs = this.renderIndexRandom();

    return (
      <div className="BC-AppInsight__DashboardV3">
        <Card
          sectioned
          title={
            <div className="header-action">
              <Heading>{i18n.translate('AdminHTML.DashboardV3.AppInsights.title')}</Heading>
              <Filter
                isShowExport={false}
                performActionSurvey={performActionSurvey}
                fromDateStateName={fromDateStateName}
                toDateStateName={toDateStateName}
                compareStateName={compareStateName}
                feature={FEATURES.APP_IMPACT}
                openReviewAddonModal={openReviewAddonModal}
                showCompare={false}
              />
            </div>
          }
          actions={[
            {
              content: i18n.translate('AdminHTML.Analytics.Common.viewReport'),
              onAction: () => history.push(routes.analytics.appImpact())
            }
          ]}>
          <div className="content">
            <div className="list">
              {this.renderItem(
                'AdminHTML.DashboardV3.AppInsights.activity',
                'AdminHTML.DashboardV3.AppInsights.activity_tooltip',
                formatNumberInThousand(totalActivityTotal),
                totalActivityPercentChange,
                totalActivityLoading,
                idxs[0]
              )}
              {this.renderItem(
                'AdminHTML.DashboardV3.AppInsights.revenue',
                'AdminHTML.DashboardV3.AppInsights.revenue_tooltip',
                formatMoney(totalRevenueTotal, moneyFormat),
                totalRevenuePercentChange,
                totalRevenueLoading,
                idxs[1]
              )}
              {this.renderItem(
                'AdminHTML.DashboardV3.AppInsights.order',
                'AdminHTML.DashboardV3.AppInsights.order_tooltip',
                formatNumberInThousand(orderCountTotal),
                totalConversionRatePercentChange,
                orderCountLoading,
                idxs[2]
              )}
              {this.renderItem(
                'AdminHTML.DashboardV3.AppInsights.cvr',
                'AdminHTML.DashboardV3.AppInsights.cvr_tooltip',
                totalConversionRateTotal,
                orderCountPercentChange,
                totalConversionRateLoading,
                idxs[3]
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  fetchAll = (fromDate, toDate, compare) => {
    const {
      totalActivityLoading,
      totalRevenueLoading,
      totalConversionRateLoading,
      orderCountLoading
    } = this.props;
    if (!totalActivityLoading) {
      this.getTotalActivity(fromDate, toDate, compare);
    }
    if (!totalRevenueLoading) {
      this.getTotalRevenue(fromDate, toDate, compare);
    }
    if (!totalConversionRateLoading) {
      this.getAppImpactConversionRate(fromDate, toDate, compare);
    }
    if (!orderCountLoading) {
      this.getOrderCount(fromDate, toDate, compare);
    }
  };

  getTotalActivity = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_ACTIVITY,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_ACTIVITY_COMPARED,
      executionIds
    );

    this.props.getTotalActivity(fromDate, toDate, compare, queryExecutionId, comparedExecutionId);
  };

  getTotalRevenue = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_REVENUE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_REVENUE_COMPARED,
      executionIds
    );

    this.props.getTotalRevenue(fromDate, toDate, compare, queryExecutionId, comparedExecutionId);
  };

  getAppImpactConversionRate = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.CONVERSION_RATE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.CONVERSION_RATE_COMPARED,
      executionIds
    );

    this.props.getAppImpactConversionRate(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getOrderCount = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.ORDER_COUNT,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.ORDER_COUNT_COMPARED,
      executionIds
    );

    this.props.getOrderCount(fromDate, toDate, compare, queryExecutionId, comparedExecutionId);
  };

  getAverageOrderValue = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.AVERAGE_ORDER_VALUE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.AVERAGE_ORDER_VALUE_COMPARED,
      executionIds
    );

    this.props.getAverageOrderValue(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getRevenuePerVisitor = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.REVENUE_PER_VISITOR,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.REVENUE_PER_VISITOR_COMPARED,
      executionIds
    );

    this.props.getRevenuePerVisitor(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  // ---------------------------------------------------------
  processUpdateExecutionId = (props, nextProps) => {
    this.processTotalActivityExecutionId(props, nextProps);
    this.processTotalRevenueExecutionId(props, nextProps);
    this.processOrderCountExecutionId(props, nextProps);
    this.processConversionRateExecutionId(props, nextProps);
  };

  processTotalActivityExecutionId = (props, nextProps) => {
    const { totalActivityQueryExecutionId, totalActivityComparedQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      totalActivityQueryExecutionId: totalActivityQueryExecutionIdNext,
      totalActivityComparedQueryExecutionId: totalActivityComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(totalActivityQueryExecutionIdNext) &&
      totalActivityQueryExecutionId !== totalActivityQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_REVENUE,
        executionId: totalActivityQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalActivityComparedQueryExecutionIdNext) &&
      totalActivityComparedQueryExecutionId !== totalActivityComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_REVENUE_COMPARED,
        executionId: totalActivityComparedQueryExecutionIdNext
      });
    }
  };
  processTotalRevenueExecutionId = (props, nextProps) => {
    const { totalRevenueQueryExecutionId, totalRevenueComparedQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      totalRevenueQueryExecutionId: totalRevenueQueryExecutionIdNext,
      totalRevenueComparedQueryExecutionId: totalRevenueComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(totalRevenueQueryExecutionIdNext) &&
      totalRevenueQueryExecutionId !== totalRevenueQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_REVENUE,
        executionId: totalRevenueQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalRevenueComparedQueryExecutionIdNext) &&
      totalRevenueComparedQueryExecutionId !== totalRevenueComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_REVENUE_COMPARED,
        executionId: totalRevenueComparedQueryExecutionIdNext
      });
    }
  };

  processConversionRateExecutionId = (props, nextProps) => {
    const { totalConversionRateQueryExecutionId, totalConversionRateComparedQueryExecutionId } =
      props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      totalConversionRateQueryExecutionId: totalConversionRateQueryExecutionIdNext,
      totalConversionRateComparedQueryExecutionId: totalConversionRateComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(totalConversionRateQueryExecutionIdNext) &&
      totalConversionRateQueryExecutionId !== totalConversionRateQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.CONVERSION_RATE,
        executionId: totalConversionRateQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalConversionRateComparedQueryExecutionIdNext) &&
      totalConversionRateComparedQueryExecutionId !==
        totalConversionRateComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.CONVERSION_RATE_COMPARED,
        executionId: totalConversionRateComparedQueryExecutionIdNext
      });
    }
  };

  processOrderCountExecutionId = (props, nextProps) => {
    const { orderCountQueryExecutionId, orderCountComparedQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      orderCountQueryExecutionId: orderCountQueryExecutionIdNext,
      orderCountComparedQueryExecutionId: orderCountComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(orderCountQueryExecutionIdNext) &&
      orderCountQueryExecutionId !== orderCountQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.ORDER_COUNT,
        executionId: orderCountQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(orderCountComparedQueryExecutionIdNext) &&
      orderCountComparedQueryExecutionId !== orderCountComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.ORDER_COUNT_COMPARED,
        executionId: orderCountComparedQueryExecutionIdNext
      });
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getTotalActivity: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getTotalActivity(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  getTotalRevenue: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getTotalRevenue(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  getAppImpactConversionRate: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getAppImpactConversionRate(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  getOrderCount: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getOrderCount(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  return {
    moneyFormat: _.get(state.oauth, 'tenantApp.externalInfo.money_format', ''),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),
    tenantApp,
    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Total activity
    totalActivityTotal: _.get(state.analyticsV2, 'totalActivityTotal', 0),
    totalActivityPercentChange: _.get(state.analyticsV2, 'totalActivityPercentChange', 0),
    totalActivityLoading: _.get(state.analyticsV2, 'totalActivityLoading', false),
    totalActivityQueryExecutionId: _.get(state.analyticsV2, 'totalActivityQueryExecutionId', ''),
    totalActivityComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'totalActivityComparedQueryExecutionId',
      ''
    ),

    // Total revenue
    totalRevenueTotal: _.get(state.analyticsV2, 'totalRevenueTotal.all', 0),
    totalRevenuePercentChange: _.get(state.analyticsV2, 'totalRevenuePercentChange.all', 0),
    totalRevenueLoading: _.get(state.analyticsV2, 'totalRevenueLoading', false),
    totalRevenueQueryExecutionId: _.get(state.analyticsV2, 'totalRevenueQueryExecutionId', ''),
    totalRevenueComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'totalRevenueComparedQueryExecutionId',
      ''
    ),

    // Total Conversion rate
    totalConversionRateTotal: _.get(state.analyticsV2, 'totalConversionRateTotal.all', 0),
    totalConversionRatePercentChange: _.get(
      state.analyticsV2,
      'totalConversionRatePercentChange.all',
      0
    ),
    totalConversionRateLoading: _.get(state.analyticsV2, 'totalConversionRateLoading', false),
    totalConversionRateQueryExecutionId: _.get(
      state.analyticsV2,
      'totalConversionRateQueryExecutionId',
      ''
    ),
    totalConversionRateComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'totalConversionRateComparedQueryExecutionId',
      ''
    ),

    // Order count
    orderCountTotal: _.get(state.analyticsV2, 'orderCountTotal.all', 0),
    orderCountPercentChange: _.get(state.analyticsV2, 'orderCountPercentChange.all', 0),
    orderCountLoading: _.get(state.analyticsV2, 'orderCountLoading', false),
    orderCountQueryExecutionId: _.get(state.analyticsV2, 'orderCountQueryExecutionId', ''),
    orderCountComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'orderCountComparedQueryExecutionId',
      ''
    )
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(AppInsight));
